import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import type { ICapHistoricalNoticesFetchRequest } from './cap.types';
import {
    fetchCapHistoricalNotices,
    resetCapHistoricalNotices,
} from '@actions/cap/capHistoricalNoticesActionCreators';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import HistoricalNoticesView from '../components/HistoricalNoticesView';

const CapHistoricalNoticesContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const userRefId = searchParams.get('referenceId') ?? '';

    const {
        hasLoaded,
        notices,
        totalCount,
        pageNumber,
        pageSize,
        sortColumn,
        sortOrder,
        hasLoadError,
    } = useAppSelector(state => state.capHistoricalNotices);

    const handleFetch = useCallback(
        (options: Partial<ICapHistoricalNoticesFetchRequest>) => {
            if (!userRefId) {
                return;
            }
            dispatch(fetchCapHistoricalNotices({ ...options, userRefId }));
        },
        [dispatch, userRefId],
    );

    useEffect(() => {
        if (!userRefId) {
            return;
        }
        handleFetch({});
    }, [handleFetch, userRefId]);

    useEffect(() => {
        return () => {
            dispatch(resetCapHistoricalNotices());
        };
    }, [dispatch]);

    return (
        <ConditionalRendering isTrue={hasLoaded}>
            <HistoricalNoticesView
                tableId='historical-notices-table'
                notices={notices}
                totalCount={totalCount}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                hasLoadError={hasLoadError}
                onFetch={handleFetch}
            />
        </ConditionalRendering>
    );
};

export default CapHistoricalNoticesContainer;
